/**
 * Reading the activation token out of the URL, once (issue #78).
 *
 * The token rides in the URL FRAGMENT, not the query string. A fragment is
 * never sent to a server and never appears in `Referer`; a query string is both.
 * The activation page makes same-origin requests, and under the browser default
 * `strict-origin-when-cross-origin` a same-origin request carries the full
 * referring URL — so `?token=` wrote the live token into the backend's own
 * access logs on every activation.
 *
 * Lives in its own module rather than beside the page component so the page
 * exports only a component (react-refresh), and so the memoisation below is
 * module state with one obvious owner.
 */

/** The read is destructive — it clears the fragment — and React is allowed to
 *  call a `useState` initialiser more than once; StrictMode does exactly that on
 *  mount in development. A second call would find the hash already cleared and
 *  return "", and which result React kept would depend on internals rather than
 *  anything documented. Caching the first answer makes the read idempotent,
 *  which is what a lazy initialiser is required to be. */
let cachedToken: string | null = null;

export function takeTokenFromFragment(): string {
  if (cachedToken !== null) return cachedToken;
  if (typeof window === "undefined") return "";

  const raw = window.location.hash.replace(/^#/, "");
  const token = new URLSearchParams(raw).get("token") ?? "";
  if (token) {
    // Clearing it closes the remaining exposure: browser history, and anything
    // that later reads location.href on this page.
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }
  cachedToken = token;
  return token;
}

/** Test seam. The cache is module state, which persists across renders within a
 *  page — correct in a browser, wrong across independent test cases. */
export function __resetActivationTokenCache(): void {
  cachedToken = null;
}

/**
 * Where the just-activated address is handed to the sign-in page.
 *
 * sessionStorage rather than a query parameter, for the same reason the token
 * rides in the fragment: a same-origin request sends the full referring URL, so
 * `?email=` would put the address in this backend's access logs on every
 * request the sign-in page makes. It is read once and cleared.
 */
export const ACTIVATED_EMAIL_KEY = "imam-lms-activated-email";

/** Memoised for the same reason `takeTokenFromFragment` is: this read is
 *  destructive, and it feeds a `useState` initialiser, which React is allowed
 *  to call more than once - StrictMode does exactly that on mount. A second
 *  call would find the key already removed and return "". */
let cachedActivatedEmail: string | null = null;

export function takeActivatedEmail(): string {
  if (cachedActivatedEmail !== null) return cachedActivatedEmail;
  if (typeof window === "undefined") return "";
  const email = window.sessionStorage.getItem(ACTIVATED_EMAIL_KEY) ?? "";
  if (email) window.sessionStorage.removeItem(ACTIVATED_EMAIL_KEY);
  cachedActivatedEmail = email;
  return email;
}

export function __resetActivatedEmailCache(): void {
  cachedActivatedEmail = null;
}
